import React, { PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useTenantConfiguration } from '../../../../hooks/use-tenant-configuration';
import { useTenantAddons, TenantAddonConstants } from '../../../../components/tenant-addons';
import { useFeatureToggles } from '../../../../feature-toggles';
import { authorityOpticStorage } from '../services/authority-optic-storage';

export const AUTHORITY_OPTIC_BESKAEFTIGELSE_STANDARD = 'beskaeftigelse-standard';
export const AUTHORITY_OPTIC_SOCIAL_STANDARD = 'social-standard';

export const AUTHORITY_OPTIC_DEFAULT = AUTHORITY_OPTIC_BESKAEFTIGELSE_STANDARD;

export type AuthorityOpticId = typeof AUTHORITY_OPTIC_BESKAEFTIGELSE_STANDARD | typeof AUTHORITY_OPTIC_SOCIAL_STANDARD;

export interface IAuthorityOpticContext {
  selectedOpticId: AuthorityOpticId | undefined;
  setSelectedOpticId: (opticId: AuthorityOpticId) => void;
}

const AuthorityOpticContext = React.createContext<IAuthorityOpticContext>({} as IAuthorityOpticContext);

function isValidOpticId(value: string): value is AuthorityOpticId {
  return value === AUTHORITY_OPTIC_BESKAEFTIGELSE_STANDARD || value === AUTHORITY_OPTIC_SOCIAL_STANDARD;
}

export const OPTIC_URL_PARAM = 'opticId';

export function AuthorityOpticProvider({ children }: PropsWithChildren) {
  const tenantConfiguration = useTenantConfiguration();
  const opticConfig = tenantConfiguration.authorityCitizenOpticSelection;
  const { isTenantAddonEnabled } = useTenantAddons();
  const isAuthorityOpticsEnabled = isTenantAddonEnabled([TenantAddonConstants.MyndighedsOptik]);
  const { search } = useLocation();
  const { isFeatureEnabled } = useFeatureToggles();
  const isDerivedDefaultEnabled = isFeatureEnabled('EnableAuthorityOpticFavoriteFix');

  // The URL param wins so new windows opened from the citizen page inherit the parent's optic
  const defaultOptic = useMemo(() => {
    const isConfigEnabled = opticConfig?.enabled === true;
    const isEnabled = isConfigEnabled && isAuthorityOpticsEnabled;

    if (!isEnabled) {
      return AUTHORITY_OPTIC_DEFAULT;
    }

    const urlOpticId = new URLSearchParams(search).get(OPTIC_URL_PARAM);
    if (urlOpticId && isValidOpticId(urlOpticId)) {
      return urlOpticId;
    }

    const favoriteOpticId = authorityOpticStorage.getFavoriteOpticId();
    if (favoriteOpticId && isValidOpticId(favoriteOpticId)) {
      return favoriteOpticId;
    }
    return (opticConfig?.defaultOptic as AuthorityOpticId) || AUTHORITY_OPTIC_DEFAULT;
  }, [opticConfig?.enabled, opticConfig?.defaultOptic, isAuthorityOpticsEnabled, search]);

  // Freezing the default at first render races the async tenant config fetch (bug 260048)
  const [frozenOpticId, setFrozenOpticId] = useState<AuthorityOpticId | undefined>(defaultOptic);
  const [userSelectedOpticId, setUserSelectedOpticId] = useState<AuthorityOpticId | undefined>(undefined);

  const selectedOpticId = isDerivedDefaultEnabled ? userSelectedOpticId ?? defaultOptic : frozenOpticId;
  const setSelectedOpticId = isDerivedDefaultEnabled ? setUserSelectedOpticId : setFrozenOpticId;

  useEffect(() => {
    if (selectedOpticId) {
      authorityOpticStorage.setActiveOpticId(selectedOpticId);
    }
  }, [selectedOpticId]);

  useEffect(() => {
    if (!isAuthorityOpticsEnabled) {
      setFrozenOpticId(AUTHORITY_OPTIC_DEFAULT);
      setUserSelectedOpticId(undefined);
    }
  }, [isAuthorityOpticsEnabled]);

  const contextValue = useMemo(
    () => ({
      selectedOpticId,
      setSelectedOpticId,
    }),
    [selectedOpticId, setSelectedOpticId]
  );

  return <AuthorityOpticContext.Provider value={contextValue}>{children}</AuthorityOpticContext.Provider>;
}

export function useAuthorityOptic(): IAuthorityOpticContext {
  return useContext(AuthorityOpticContext);
}

export function useSelectedAuthorityOptic(): AuthorityOpticId | undefined {
  const { selectedOpticId } = useAuthorityOptic();
  return selectedOpticId;
}

export function useOpticUrlParamWhenNonDefault(): AuthorityOpticId | undefined {
  const { selectedOpticId } = useAuthorityOptic();
  if (!selectedOpticId || selectedOpticId === AUTHORITY_OPTIC_DEFAULT) {
    return undefined;
  }
  return selectedOpticId;
}
